import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Dialog } from "@material-ui/core";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import { useParams, useHistory } from 'react-router-dom';

import { blue100, gray010, gray300, green100, yellow400 } from "../../../../uiKit/assets/colors/colors";
import CustomDialog, { useStylesDialog } from "../../../../uiKit/CustomDialog/customDialog";
import CustomButton from "../../../../uiKit/Button/CustomButton";
import Title from "../../../../uiKit/Title/Title";
import CustomInput from "../../../../uiKit/Input/CustomInput";
import { MailMan } from "../../../../uiKit/assets/svg/mailMan";
import { validateDigit } from "../../../../helpers/validation";

import { getCampaignBalanceDetails, requestPartialFunds } from "../../../../redux/state/paymentSlice";
import { stopCampaign } from "../../../../redux/state/campaignSlice";

const styles = {
    position: 'absolute',
    top: 0,
    height: '16px',
    float: 'right',
    borderRight: `2px solid ${gray010}`,
    borderRadius: '4px 0 0 4px',
}

const CampaignBalanceInfo = () => {
    const dispatch = useDispatch();
    const { campaignId, communityId } = useParams();

    const campaignBalance = useSelector(state => state.payment.campaignBalanceDetailed, shallowEqual);
    const isLoading = useSelector(state => state.payment.isLoading.getCampaignBalance, shallowEqual);
    const campaign = useSelector(state => state.campaign.campaign, shallowEqual);

    const target = campaign.amount;

    const sentWidth = `${campaignBalance?.sentValue}%`;
    const sentValue = `$${campaignBalance?.totalSent.toLocaleString()}`;

    const pendingWidth = `${campaignBalance?.pendingValue}%`;
    const pendingMargin = `${campaignBalance?.sentValue}%`;
    const pendingValue = `$${campaignBalance?.totalRequestedForPartial.toLocaleString()}`;

    const raisedWidth = `${campaignBalance?.totalCollected > target && campaignBalance?.sentValue < 100 ?
        100 - campaignBalance?.sentValue :
        campaignBalance?.progressValue}%`
    const raisedMargin = `${campaignBalance?.sentValue < 100 ? campaignBalance?.sentValue : 0}%`;
    const raisedValue = `$${campaignBalance?.totalCollected > target ?
        target.toLocaleString() :
        campaignBalance?.totalCollected.toLocaleString()}`;

    const fetchData = useCallback(() => {
        if (!campaignId || !communityId || !target) return;
        dispatch(getCampaignBalanceDetails({ communityId, campaignId, target }))
    }, [campaignId, communityId, target, dispatch])

    useEffect(() => {
        fetchData();
    }, [fetchData])

    return !isLoading ? (
        <div>
            <div style={{
                margin: '12px 0',
                width: '100%',
                display: 'flex',
            }}>
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '16px',
                }}>
                    {
                        !!campaignBalance?.totalSent &&
                        campaignBalance?.totalSent < target &&
                        <div style={{
                            backgroundColor: green100,
                            width: sentWidth,
                            left: 0,
                            zIndex: 1,
                            ...styles,
                        }} >
                            <Title
                                text={sentValue}
                                color={gray010}
                                style={{ float: 'right', margin: '1px 8px' }}
                                lineHeight={14}
                            />
                        </div>
                    }

                    {
                        !!campaignBalance?.totalRequestedForPartial &&
                        campaignBalance?.totalRequestedForPartial < target &&
                        <div style={{
                            backgroundColor: yellow400,
                            width: pendingWidth,
                            left: pendingMargin,
                            zIndex: 2,
                            ...styles,
                        }} >
                            <Title
                                text={pendingValue}
                                color={gray010}
                                style={{ float: 'right', margin: '1px 8px' }}
                                lineHeight={14}
                            />
                        </div>
                    }
                    {
                        !isLoading &&
                        !!campaignBalance?.totalCollected &&
                        <div style={{
                            width: raisedWidth,
                            backgroundColor: blue100,
                            left: raisedMargin,
                            zIndex: 1,
                            ...styles,
                        }} >
                            <Title
                                text={raisedValue}
                                color={gray010}
                                style={{ float: 'right', margin: '1px 8px' }}
                                lineHeight={14}
                            />
                        </div>
                    }
                    {campaignBalance?.totalCollected < target && <div style={{
                        width: '100%',
                        backgroundColor: gray300,
                        left: 0,
                        ...styles,
                        borderRadius: '4px',
                    }} >
                        <Title
                            text={`$${target}`}
                            lineHeight={14}
                            style={{ float: 'right', margin: '1px 8px' }}
                        />
                    </div>
                    }
                </div>
                {
                    campaignBalance?.surplus > 0 && <Title
                        lineHeight={16}
                        color={green100}
                        text={`+$${campaignBalance?.surplus.toLocaleString()}`}
                        style={{ minWidth: '50px', overflow: 'visible' }}
                    />
                }
            </div>
            <div className="campaignInfoWrapper" >
                <div className="campaignBalanceInfo" >
                    {!!campaignBalance?.totalSent && <div style={{
                        display: 'flex'
                    }}>
                        <Title text={sentValue} />
                        <Title
                            text={` sent`}
                            fontWeight={400}
                            style={{
                                marginLeft: '4px'
                            }}
                        />
                    </div>}

                    {!!campaignBalance?.totalRequestedForPartial && <div style={{
                        display: 'flex'
                    }}>

                        <Title text={pendingValue} />
                        <Title
                            text={` pending payment`}
                            fontWeight={400}
                            style={{
                                marginLeft: '4px'
                            }}
                        />
                    </div>}
                    <div style={{
                        display: 'flex'
                    }}>
                        <Title text={`$${campaignBalance?.totalCollected.toLocaleString()}`} />
                        <Title
                            text={`collected of $${target.toLocaleString()}`}
                            fontWeight={400}
                            style={{
                                marginLeft: '4px'
                            }}
                        />
                    </div>
                </div>
                <div className="campaignAdminButtons">
                    <SendPartialFunds
                        balance={campaignBalance?.netAvailableForPartial}
                        campaignName={campaign.name}
                        fetchData={fetchData}
                    />
                    <StopCampaing />
                </div>
            </div>
        </div>
    ) : <div style={{ height: '108px' }} />
}

const StopCampaing = () => {
    const dispatch = useDispatch();
    const { campaignId, communityId } = useParams();
    const history = useHistory();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const campaignBalance = useSelector(state => state.payment.campaignBalanceDetailed, shallowEqual);
    const campaign = useSelector(state => state.campaign.campaign, shallowEqual);
    const isLoadingStopCampaign = useSelector(state => state.campaign.isLoading.stopCampaign, shallowEqual);

    const stopCampaignHandler = () => {
        if (!!communityId && !!campaignId) {
            dispatch(stopCampaign({ communityId, campaignId }))
                .then(unwrapResult)
                .then(() => history.push(`/campaigns/campaign/completed/${communityId}/${campaignId}`))
                .catch(({ title }) => toast.info(title))
                .finally(() => setConfirmOpen(false))
        }
    }

    return (
        <>
            <CustomButton
                title='Close Campign'
                isWhite
                onClick={() => setConfirmOpen(true)}
                disabled={!campaignBalance?.totalCollected}
            />

            <CustomDialog
                confirmOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                Image={MailMan}
                mainButtonTitle='Confirm'
                mainButtonAction={stopCampaignHandler}
                isLoading={isLoadingStopCampaign}
                secondButton={true}
                title={`Please note, this action will stop the ${campaign.name}.`}
                firstDescription={`You're sending $${campaignBalance?.balance.toLocaleString()} to recipient. Please note that the amount the recipient will actually receive will be $${campaignBalance?.netBalance.toLocaleString()} (after the credit card processing fees are deducted)`}
                secondDescription={`Do you confirm this action?`}
            />
        </>
    )
}

const SendPartialFunds = ({ balance, campaignName, fetchData }) => {
    const dispatch = useDispatch();
    const { campaignId, communityId } = useParams();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [amountError, setAmountError] = useState("");

    const dialogClasses = useStylesDialog();

    const onClose = () => setConfirmOpen(false);

    const requestPartialFundsHandler = () => {
        if (!communityId || !campaignId || !amount) return;
        dispatch(requestPartialFunds({ communityId, campaignId, amount }))
            .then(unwrapResult)
            .then(() => {
                fetchData();
                toast.info("Success");
                setAmountError("");
                onClose();
            })
            .catch(({ title, errors }) => {
                toast.info(title);
                if (!!errors) {
                    const list = Object.values(errors).join(`\n `);
                    setAmountError(list);
                }
            })
    }

    const onPartialFundChange = (event) => {
        const value = event.target.value;
        if (!validateDigit(value)) {
            setAmount("");
            return
        }
        setAmount(value);
    }

    return (
        <>
            <CustomButton
                title='Send partial funds'
                onClick={() => setConfirmOpen(true)}
            />
            <Dialog
                open={confirmOpen}
                onClose={onClose}
                disableScrollLock={true}
                classes={dialogClasses}
            >
                <div className='page'>
                    <div className='feedback feedbackContainer'>
                        <div className='feedbackImageContainer'>
                            <MailMan />
                        </div>
                    </div>
                    <Title
                        text={`Please note, this action won’t stop the ${campaignName}.`}
                        fontSize={16}
                        lineHeight={20}
                        style={{ marginBottom: '16px' }}
                    />
                    <Title
                        text={'Specify the amount of funds you’d like to send.'}
                        fontWeight={400}
                        style={{ marginBottom: '16px' }}
                    />
                    <CustomInput
                        value={amount}
                        onChange={onPartialFundChange}
                        description={`Available Campaign Balance: $${Math.floor(balance)}`}
                        inputMode="numeric"
                        error={!!amountError}
                        errorMessage={amountError}
                    />
                    <div style={{ marginTop: '24px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CustomButton
                            onClick={requestPartialFundsHandler}
                            title='Send partial funds'
                        />
                        <CustomButton
                            onClick={onClose}
                            title='Decline'
                            isWhite
                            style={{ marginTop: '16px' }}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default CampaignBalanceInfo;